"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { getToken } from "../../utils/auth.ts"
import axiosInstance from "../../utils/axiosConfig.ts"
import { toast } from "../../utils/notify.ts";
import { createFormDataRequestConfig } from "../../utils/crudSubmit.ts"
import { isAllowedResolutionProofImage } from "../../utils/customerPortalComplaint.ts"
import {
  AlertCircle,
  Clock,
  CheckCircle,
  User,
  Phone,
  MapPin,
  ChevronRight,
  MessageSquare,
  Image,
  Star,
  Hash,
  Cable,
  Package,
  Banknote,
  Plus,
  Paperclip,
} from "lucide-react"
import { PortalStatStrip, type PortalStatItem } from "./shared/PortalStatStrip.tsx"
import { PortalSegmentedControl } from "./shared/PortalSegmentedControl.tsx"
import { PortalSheet } from "./shared/PortalSheet.tsx"
import { PortalStatusPill, portalStatusAvatar } from "./shared/PortalStatusPill.tsx"
import { ComplaintFilePreview } from "../complaint/ComplaintFilePreview.tsx"

interface Complaint {
  id: string
  ticket_number: string
  description: string | null
  status: string
  created_at: string | null
  updated_at: string | null
  resolved_at: string | null
  response_due_date: string | null
  resolution_attempts: number
  satisfaction_rating: number | null
  resolution_proof: string | null
  remarks: string | null
  attachment_path: string | null
  feedback_comments: string | null
  customer_id: string | null
  customer_name: string | null
  customer_phone: string | null
  customer_address: string | null
  customer_area: string | null
  customer_internet_id: string | null
  assigned_to?: string | null
  assigned_to_name?: string | null
  is_unassigned?: boolean
  is_assignee?: boolean
  can_update?: boolean
}

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "completed", label: "Completed" },
]

interface CustodyItem {
  assignment_id: string
  inventory_item_id: string
  item_type: string | null
  quantity: number
  unit_price: number
  unit_of_measure: string
  is_wire?: boolean
}

interface ComplaintCustomerOption {
  id: string
  name: string
  internet_id: string | null
  phone: string | null
  is_active: boolean
}

interface ComplaintCategoryOption {
  value: string
  label: string
}

function isWireCustody(item: CustodyItem) {
  if (typeof item.is_wire === "boolean") return item.is_wire
  const t = (item.item_type || "").trim().toLowerCase()
  return t === "fiber cable" || t === "fiber_cable" || t === "cable" || t === "wire"
}

function todayPktDate() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" })
}

export function PortalComplaints() {
  const [complaints, setComplaints] = useState<Complaint[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>("all")
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null)
  const [updating, setUpdating] = useState(false)
  const [resolutionForm, setResolutionForm] = useState({
    status: "",
    remarks: "",
  })
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [proofPreview, setProofPreview] = useState<string | null>(null)
  const [custody, setCustody] = useState<CustodyItem[]>([])
  const [wireAssignmentId, setWireAssignmentId] = useState("")
  const [wireQty, setWireQty] = useState("")
  const [selectedCustodyIds, setSelectedCustodyIds] = useState<string[]>([])
  const [cashAmount, setCashAmount] = useState("")
  const [registrationOpen, setRegistrationOpen] = useState(false)
  const [registrationOptionsLoading, setRegistrationOptionsLoading] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [customerOptions, setCustomerOptions] = useState<ComplaintCustomerOption[]>([])
  const [categoryOptions, setCategoryOptions] = useState<ComplaintCategoryOption[]>([])
  const [registrationForm, setRegistrationForm] = useState({
    customer_id: "",
    category: "no_internet",
    description: "",
  })
  const [registrationAttachment, setRegistrationAttachment] = useState<File | null>(null)

  useEffect(() => {
    fetchComplaints()
  }, [filter])

  const fetchComplaints = async () => {
    setLoading(true)
    try {
      const token = getToken()
      const params = filter !== "all" ? `?status=${filter}` : ""
      const response = await axiosInstance.get(`/employee-portal/complaints${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setComplaints(response.data)
    } catch (error) {
      console.error("Failed to fetch complaints:", error)
      toast.error("Failed to load complaints")
    } finally {
      setLoading(false)
    }
  }

  const openRegistration = async () => {
    setRegistrationOpen(true)
    if (customerOptions.length > 0 || registrationOptionsLoading) return
    setRegistrationOptionsLoading(true)
    try {
      const token = getToken()
      const response = await axiosInstance.get("/employee-portal/complaints/create-options", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const customers = Array.isArray(response.data?.customers) ? response.data.customers : []
      const categories = Array.isArray(response.data?.categories) ? response.data.categories : []
      setCustomerOptions(customers)
      setCategoryOptions(categories)
      if (categories.length > 0 && !categories.some((item: ComplaintCategoryOption) => item.value === registrationForm.category)) {
        setRegistrationForm((current) => ({ ...current, category: categories[0].value }))
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to load assigned customers")
      setRegistrationOpen(false)
    } finally {
      setRegistrationOptionsLoading(false)
    }
  }

  const closeRegistration = () => {
    if (registering) return
    setRegistrationOpen(false)
    setRegistrationAttachment(null)
  }

  const handleRegisterComplaint = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!registrationForm.customer_id) {
      toast.error("Select an assigned customer")
      return
    }
    if (!registrationForm.description.trim()) {
      toast.error("Enter a complaint description")
      return
    }
    if (registrationAttachment && registrationAttachment.size > 16 * 1024 * 1024) {
      toast.error("Attachment must be 16 MB or smaller")
      return
    }

    setRegistering(true)
    try {
      const token = getToken()
      const body = new FormData()
      body.append("customer_id", registrationForm.customer_id)
      body.append("category", registrationForm.category)
      body.append("description", registrationForm.description.trim())
      if (registrationAttachment) {
        body.append("attachment", registrationAttachment, registrationAttachment.name)
      }
      const response = await axiosInstance.post(
        "/employee-portal/complaints",
        body,
        createFormDataRequestConfig(token)
      )
      toast.success(`Complaint ${response.data?.ticket_number || ""} registered successfully`.trim())
      setRegistrationForm({ customer_id: "", category: "no_internet", description: "" })
      setRegistrationAttachment(null)
      setRegistrationOpen(false)
      setFilter("all")
      await fetchComplaints()
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to register complaint")
    } finally {
      setRegistering(false)
    }
  }

  const clearProofFile = () => {
    if (proofPreview) URL.revokeObjectURL(proofPreview)
    setProofFile(null)
    setProofPreview(null)
  }

  const resetBillingFields = () => {
    setWireAssignmentId("")
    setWireQty("")
    setSelectedCustodyIds([])
    setCashAmount("")
  }

  const loadResolveOptions = async () => {
    try {
      const token = getToken()
      const res = await axiosInstance.get("/employee-portal/complaints/resolve-options", {
        headers: { Authorization: `Bearer ${token}` },
      })
      setCustody(res.data?.custody || [])
    } catch {
      setCustody([])
    }
  }

  const handleProofFileChange = (file: File | null) => {
    if (proofPreview) URL.revokeObjectURL(proofPreview)
    if (!file) {
      setProofFile(null)
      setProofPreview(null)
      return
    }
    if (!isAllowedResolutionProofImage(file.name)) {
      toast.error("Proof must be a PNG, JPG, JPEG, GIF, or WebP image")
      return
    }
    setProofFile(file)
    setProofPreview(URL.createObjectURL(file))
  }

  const buildMaterials = () => {
    const materials: Array<Record<string, unknown>> = []
    const wQty = Number(wireQty)
    if (wireAssignmentId && wQty > 0) {
      const row = custody.find((c) => c.assignment_id === wireAssignmentId)
      if (row) {
        materials.push({
          inventory_item_id: row.inventory_item_id,
          assignment_id: row.assignment_id,
          quantity: wQty,
          usage_outcome: "consumed",
          unit_price: row.unit_price,
        })
      }
    }
    for (const assignmentId of selectedCustodyIds) {
      const row = custody.find((c) => c.assignment_id === assignmentId)
      if (!row) continue
      materials.push({
        inventory_item_id: row.inventory_item_id,
        assignment_id: row.assignment_id,
        quantity: 1,
        usage_outcome: "installed",
        unit_price: row.unit_price,
      })
    }
    return materials
  }

  const handleStatusUpdate = async () => {
    if (!selectedComplaint || !resolutionForm.status) return
    setUpdating(true)
    try {
      const token = getToken()
      const materials = resolutionForm.status === "resolved" ? buildMaterials() : []
      const cash = resolutionForm.status === "resolved" ? Number(cashAmount || 0) : 0
      const hasBilling = materials.length > 0 || cash > 0

      if (cash > 0 && materials.length === 0) {
        toast.error("Enter parts used or set cash received to 0")
        return
      }

      if (proofFile || hasBilling) {
        const body = new FormData()
        body.append("status", resolutionForm.status)
        if (resolutionForm.remarks) body.append("remarks", resolutionForm.remarks)
        if (proofFile) body.append("resolution_proof", proofFile, proofFile.name)
        if (hasBilling) {
          body.append("materials", JSON.stringify(materials))
          body.append("cash_amount", String(cash || 0))
          if (cash > 0) {
            body.append("payment_method", "cash")
            body.append("payment_date", todayPktDate())
          }
        }
        await axiosInstance.put(
          `/employee-portal/complaints/${selectedComplaint.id}/status`,
          body,
          createFormDataRequestConfig(token)
        )
      } else {
        await axiosInstance.put(
          `/employee-portal/complaints/${selectedComplaint.id}/status`,
          {
            status: resolutionForm.status,
            remarks: resolutionForm.remarks || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      }
      toast.success(hasBilling ? "Complaint resolved and billed" : "Complaint updated successfully!")
      clearProofFile()
      resetBillingFields()
      setSelectedComplaint(null)
      fetchComplaints()
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update complaint")
    } finally {
      setUpdating(false)
    }
  }

  const openComplaintModal = (complaint: Complaint) => {
    clearProofFile()
    resetBillingFields()
    setSelectedComplaint(complaint)
    setResolutionForm({
      status: complaint.status,
      remarks: complaint.remarks || "",
    })
    if (complaint.can_update && !["resolved", "completed", "closed"].includes(complaint.status)) {
      loadResolveOptions()
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-14 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-9 bg-gray-200 rounded-lg animate-pulse w-64" />
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
        {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse" />
        ))}
        </div>
      </div>
    )
  }

  const ratedComplaints = complaints.filter((c) => c.satisfaction_rating)
  const avgRating = ratedComplaints.length
    ? (ratedComplaints.reduce((sum, c) => sum + (c.satisfaction_rating || 0), 0) / ratedComplaints.length).toFixed(1)
    : "—"

  const statItems: PortalStatItem[] = [
    { key: "open", label: "Open", value: complaints.filter((c) => c.status === "open").length, icon: AlertCircle, tone: "danger" },
    {
      key: "in_progress",
      label: "In progress",
      value: complaints.filter((c) => c.status === "in_progress").length,
      icon: Clock,
      tone: "accent",
    },
    {
      key: "resolved",
      label: "Resolved",
      value: complaints.filter((c) => c.status === "resolved").length,
      icon: CheckCircle,
      tone: "success",
    },
    { key: "avg_rating", label: "Avg rating", value: avgRating, icon: Star, tone: "default" },
  ]

  const detailTitle = selectedComplaint ? `#${selectedComplaint.ticket_number}` : ""
  const detailSubtitle = selectedComplaint?.customer_name || undefined

  const canUpdateSelected =
    !!selectedComplaint &&
    Boolean(selectedComplaint.can_update) &&
    selectedComplaint.status !== "resolved" &&
    selectedComplaint.status !== "completed" &&
    selectedComplaint.status !== "closed"

  const detailFooter = selectedComplaint && (
    <>
          <button
        onClick={() => setSelectedComplaint(null)}
        className="h-9 px-4 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-100 transition-colors"
      >
        Close
          </button>
      {canUpdateSelected && (
        <button
          onClick={handleStatusUpdate}
          disabled={updating}
          className="h-9 px-4 rounded-lg bg-portal-primary text-sm font-medium text-white hover:bg-portal-primary-dark transition-colors disabled:opacity-50"
        >
          {updating ? "Updating..." : "Update complaint"}
        </button>
      )}
    </>
  )

  const detailBody = selectedComplaint && (
    <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <PortalStatusPill status={selectedComplaint.status} />
              {selectedComplaint.is_assignee ? (
                <span className="rounded-full bg-electric-blue/10 px-2 py-0.5 text-[10px] font-medium text-electric-blue">
                  Assigned to you
                </span>
              ) : selectedComplaint.is_unassigned ? (
                <span className="rounded-full bg-coral-red/10 px-2 py-0.5 text-[10px] font-medium text-coral-red">
                  Unassigned
                </span>
              ) : (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  View only
                  </span>
              )}
                  {selectedComplaint.satisfaction_rating && (
                <div className="ml-auto flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                      className={`w-4 h-4 ${
                        i < selectedComplaint.satisfaction_rating! ? "text-amber-500 fill-current" : "text-gray-300"
                      }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-gray-500">Assignee</p>
                <p className="font-medium text-gray-900">
                  {selectedComplaint.is_unassigned
                    ? "Unassigned"
                    : selectedComplaint.assigned_to_name || (selectedComplaint.is_assignee ? "You" : "Another employee")}
                </p>
              </div>
                  <div>
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.created_at ? new Date(selectedComplaint.created_at).toLocaleString() : "—"}
                    </p>
                  </div>
                  {selectedComplaint.resolved_at && (
                    <div>
                      <p className="text-gray-500">Resolved</p>
                  <p className="font-medium text-emerald-600">
                        {new Date(selectedComplaint.resolved_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Attempts</p>
                    <p className="font-medium text-gray-900">{selectedComplaint.resolution_attempts}</p>
                  </div>
                  {selectedComplaint.response_due_date && (
                    <div>
                  <p className="text-gray-500">Due by</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedComplaint.response_due_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

            {selectedComplaint.description && (
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Description</p>
                <p className="text-gray-700">{selectedComplaint.description}</p>
                </div>
              )}

              {selectedComplaint.customer_name && (
              <div className="space-y-2 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</p>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <User className="w-3.5 h-3.5" /> Name
                  </span>
                  <span className="font-medium text-gray-900">{selectedComplaint.customer_name}</span>
                    </div>
                    {selectedComplaint.customer_internet_id && (
                  <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-600">Internet ID</span>
                      <span className="font-medium text-gray-900">{selectedComplaint.customer_internet_id}</span>
                  </div>
                    )}
                    {selectedComplaint.customer_phone && (
                  <div className="flex items-center justify-between">
                        <span className="text-gray-600">Phone</span>
                    <a
                      href={`tel:${selectedComplaint.customer_phone}`}
                      className="flex items-center gap-1 font-medium text-electric-blue"
                    >
                          <Phone className="w-3 h-3" />
                          {selectedComplaint.customer_phone}
                        </a>
                      </div>
                    )}
                    {selectedComplaint.customer_area && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <MapPin className="w-3.5 h-3.5" /> Area
                    </span>
                        <span className="font-medium">{selectedComplaint.customer_area}</span>
                      </div>
                    )}
                    {selectedComplaint.customer_address && (
                      <div>
                        <span className="text-gray-600">Address</span>
                    <p className="mt-1 font-medium">{selectedComplaint.customer_address}</p>
                      </div>
                    )}
                  </div>
            )}

            {selectedComplaint.remarks && (
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                  <MessageSquare className="w-3.5 h-3.5" /> Resolution remarks
                </p>
                <p className="text-gray-700">{selectedComplaint.remarks}</p>
              </div>
            )}

            {selectedComplaint.feedback_comments && (
              <div className="border-t border-gray-100 pt-3">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <MessageSquare className="w-3.5 h-3.5" /> Customer feedback
                </p>
                <p className="text-gray-700">{selectedComplaint.feedback_comments}</p>
                </div>
              )}

            {(selectedComplaint.attachment_path || selectedComplaint.resolution_proof) && (
              <div className="space-y-2 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Attachments</p>
                {selectedComplaint.attachment_path && (
                  <ComplaintFilePreview
                    label="Complaint attachment"
                    filePath={selectedComplaint.attachment_path}
                    fetchUrl={`/employee-portal/complaints/${selectedComplaint.id}/attachment`}
                    actionClassName="text-electric-blue"
                  />
                )}
                {selectedComplaint.resolution_proof && (
                  <ComplaintFilePreview
                    label="Resolution proof"
                    filePath={selectedComplaint.resolution_proof}
                    fetchUrl={`/employee-portal/complaints/${selectedComplaint.id}/resolution-proof`}
                    actionClassName="text-electric-blue"
                  />
                )}
                </div>
              )}

            {selectedComplaint.status !== "resolved" &&
              selectedComplaint.status !== "completed" &&
              selectedComplaint.status !== "closed" &&
              !canUpdateSelected && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {selectedComplaint.is_unassigned
                    ? "This ticket is unassigned. Only the company owner can assign it."
                    : "You can view this ticket, but only the assigned technician can update it."}
                </div>
              )}

            {canUpdateSelected && (
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Update complaint</p>
                  
                  <div>
                  <label className="text-xs font-medium text-gray-600">Status</label>
                    <select
                      value={resolutionForm.status}
                      onChange={(e) => setResolutionForm({ ...resolutionForm, status: e.target.value })}
                    className="mt-1 w-full h-9 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-portal-accent/40 focus:border-portal-accent"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  {resolutionForm.status === "resolved" && (
                    <>
                      <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                        <MessageSquare className="w-3.5 h-3.5" />
                        Resolution remarks
                        </label>
                        <textarea
                          value={resolutionForm.remarks}
                          onChange={(e) => setResolutionForm({ ...resolutionForm, remarks: e.target.value })}
                          placeholder="Describe how the issue was resolved..."
                          rows={3}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-portal-accent/40 focus:border-portal-accent resize-none"
                        />
                      </div>

                      <div>
                      <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                        <Image className="w-3.5 h-3.5" />
                        Resolution proof
                      </p>
                      <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center hover:bg-gray-100">
                        {proofPreview ? (
                          <img
                            src={proofPreview}
                            alt="Resolution proof preview"
                            className="mb-2 max-h-32 rounded-md object-contain"
                          />
                        ) : (
                          <Image className="mb-1 h-5 w-5 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-600">
                          {proofFile ? proofFile.name : "Upload image (PNG, JPG, GIF, WebP)"}
                        </span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          className="sr-only"
                          aria-label="Upload resolution proof"
                          onChange={(e) => handleProofFileChange(e.target.files?.[0] || null)}
                        />
                      </label>
                      {proofFile && (
                        <button
                          type="button"
                          onClick={clearProofFile}
                          className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Remove image
                        </button>
                      )}
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <Cable className="w-3.5 h-3.5" />
                          Wire used (from your custody)
                        </p>
                        <select
                          value={wireAssignmentId}
                          onChange={(e) => setWireAssignmentId(e.target.value)}
                          className="w-full h-9 px-2 text-xs border border-gray-300 rounded-lg"
                        >
                          <option value="">No wire</option>
                          {custody.filter(isWireCustody).map((w) => (
                            <option key={w.assignment_id} value={w.assignment_id}>
                              {w.item_type} · qty {w.quantity}
                              {w.unit_of_measure ? ` ${w.unit_of_measure}` : ""} · Rs{" "}
                              {Number(w.unit_price || 0).toLocaleString()}
                            </option>
                          ))}
                        </select>
                        {wireAssignmentId && (
                          <input
                            type="number"
                            min={1}
                            max={
                              custody.find((c) => c.assignment_id === wireAssignmentId)?.quantity ||
                              undefined
                            }
                            value={wireQty}
                            onChange={(e) => setWireQty(e.target.value)}
                            placeholder="Quantity (m / units)"
                            className="w-full h-9 px-2 text-xs border border-gray-300 rounded-lg"
                          />
                        )}
                        {custody.filter(isWireCustody).length === 0 && (
                          <p className="text-xs text-slate-400">No wire/cable in your custody</p>
                        )}
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <Package className="w-3.5 h-3.5" />
                          Other inventory (your custody)
                        </p>
                        {custody.filter((c) => !isWireCustody(c)).length === 0 ? (
                          <p className="text-xs text-slate-400">No assigned equipment in custody</p>
                        ) : (
                          <div className="max-h-36 overflow-y-auto space-y-1">
                            {custody.filter((c) => !isWireCustody(c)).map((c) => {
                              const selected = selectedCustodyIds.includes(c.assignment_id)
                              return (
                                <button
                                  key={c.assignment_id}
                                  type="button"
                                  onClick={() =>
                                    setSelectedCustodyIds((prev) =>
                                      selected
                                        ? prev.filter((id) => id !== c.assignment_id)
                                        : [...prev, c.assignment_id]
                                    )
                                  }
                                  className={`w-full text-left px-2 py-1.5 rounded-md text-xs border ${
                                    selected
                                      ? "border-portal-accent bg-portal-accent/5"
                                      : "border-slate-200"
                                  }`}
                                >
                                  {c.item_type || "Item"} · qty {c.quantity} · Rs{" "}
                                  {Number(c.unit_price || 0).toLocaleString()}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <Banknote className="w-3.5 h-3.5" />
                          Cash received from customer (optional)
                        </label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={cashAmount}
                          onChange={(e) => setCashAmount(e.target.value)}
                          placeholder="0"
                          className="mt-1 w-full h-9 px-3 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
    )

  return (
    <div className="lg:grid lg:grid-cols-[360px_1fr] lg:items-start lg:gap-4">
      <div className="space-y-3">
        <PortalStatStrip items={statItems} columnsMobile={2} columnsDesktop={2} />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <PortalSegmentedControl options={FILTER_OPTIONS} value={filter} onChange={setFilter} />
          <button
            type="button"
            onClick={openRegistration}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-portal-primary px-3 text-sm font-medium text-white transition-colors hover:bg-portal-primary-dark"
          >
            <Plus className="h-4 w-4" />
            Register complaint
          </button>
        </div>

        {complaints.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-sm">
            <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No complaints found</p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-100 overflow-hidden">
            {complaints.map((complaint) => {
              const avatar = portalStatusAvatar(complaint.status)
              const isSelected = selectedComplaint?.id === complaint.id

              return (
                <div
                  key={complaint.id}
                  onClick={() => openComplaintModal(complaint)}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    isSelected ? "bg-portal-tint border-l-2 border-l-electric-blue" : "hover:bg-gray-50 active:bg-gray-100 border-l-2 border-l-transparent"
                  }`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${avatar.bg}`}>
                    <AlertCircle className={`w-4 h-4 ${avatar.text}`} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-electric-blue">
                        <Hash className="w-3 h-3" />
                        {complaint.ticket_number}
                      </span>
                      {complaint.is_assignee && (
                        <span className="rounded bg-electric-blue/10 px-1.5 py-0.5 text-[10px] font-medium text-electric-blue">
                          Yours
                        </span>
                      )}
                      {!complaint.is_assignee && !complaint.is_unassigned && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                          View
                        </span>
                      )}
                      {complaint.satisfaction_rating && (
                        <span className="flex shrink-0 items-center gap-0.5 text-amber-500">
                          {[...Array(complaint.satisfaction_rating)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-gray-700">
                      {complaint.customer_name || complaint.description || "No description"}
                      {complaint.customer_area ? ` · ${complaint.customer_area}` : ""}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {complaint.created_at ? new Date(complaint.created_at).toLocaleDateString() : "—"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 lg:hidden" />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="hidden lg:sticky lg:top-6 lg:flex lg:max-h-[calc(100vh-120px)] lg:flex-col lg:rounded-xl lg:border lg:border-gray-100 lg:bg-white lg:shadow-sm">
        {selectedComplaint ? (
          <>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-gray-900">{detailTitle}</h3>
                {detailSubtitle && <p className="truncate text-xs text-gray-500">{detailSubtitle}</p>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">{detailBody}</div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3">
              {detailFooter}
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
            <AlertCircle className="h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-400">Select a complaint to view details</p>
          </div>
        )}
      </div>

      <PortalSheet
        open={!!selectedComplaint}
        onClose={() => setSelectedComplaint(null)}
        title={detailTitle}
        subtitle={detailSubtitle}
        footer={detailFooter}
        hideOnDesktop
      >
        {detailBody}
      </PortalSheet>

      <PortalSheet
        open={registrationOpen}
        onClose={closeRegistration}
        title="Register complaint"
        subtitle="For a customer assigned to you"
        maxWidthClassName="lg:max-w-lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeRegistration}
              disabled={registering}
              className="h-9 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="employee-complaint-registration"
              disabled={registering || registrationOptionsLoading || customerOptions.length === 0}
              className="h-9 rounded-lg bg-portal-primary px-4 text-sm font-medium text-white hover:bg-portal-primary-dark disabled:opacity-50"
            >
              {registering ? "Registering..." : "Register complaint"}
            </button>
          </>
        }
      >
        <form id="employee-complaint-registration" onSubmit={handleRegisterComplaint} className="space-y-4">
          {registrationOptionsLoading ? (
            <div className="space-y-3" aria-label="Loading assigned customers">
              <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
              <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
              <div className="h-28 animate-pulse rounded-lg bg-gray-100" />
            </div>
          ) : customerOptions.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
              No customers are currently assigned to you.
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="complaint-customer" className="mb-1 block text-sm font-medium text-gray-700">
                  Customer <span className="text-coral-red">*</span>
                </label>
                <select
                  id="complaint-customer"
                  value={registrationForm.customer_id}
                  onChange={(event) => setRegistrationForm((current) => ({ ...current, customer_id: event.target.value }))}
                  required
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/20"
                >
                  <option value="">Select an assigned customer</option>
                  {customerOptions.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}{customer.internet_id ? ` · ${customer.internet_id}` : ""}{!customer.is_active ? " (inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="complaint-category" className="mb-1 block text-sm font-medium text-gray-700">
                  Category <span className="text-coral-red">*</span>
                </label>
                <select
                  id="complaint-category"
                  value={registrationForm.category}
                  onChange={(event) => setRegistrationForm((current) => ({ ...current, category: event.target.value }))}
                  required
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/20"
                >
                  {(categoryOptions.length > 0 ? categoryOptions : [{ value: "other", label: "Other" }]).map((category) => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="complaint-description" className="mb-1 block text-sm font-medium text-gray-700">
                  Description <span className="text-coral-red">*</span>
                </label>
                <textarea
                  id="complaint-description"
                  value={registrationForm.description}
                  onChange={(event) => setRegistrationForm((current) => ({ ...current, description: event.target.value }))}
                  required
                  maxLength={5000}
                  rows={5}
                  placeholder="Describe the customer's issue..."
                  className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/20"
                />
                <p className="mt-1 text-right text-xs text-gray-400">{registrationForm.description.length}/5000</p>
              </div>

              <div>
                <label htmlFor="complaint-attachment" className="mb-1 block text-sm font-medium text-gray-700">
                  Attachment <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <label
                  htmlFor="complaint-attachment"
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-electric-blue hover:bg-portal-tint"
                >
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="truncate">{registrationAttachment?.name || "Choose PDF or image (max 16 MB)"}</span>
                </label>
                <input
                  id="complaint-attachment"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.gif"
                  className="sr-only"
                  onChange={(event) => setRegistrationAttachment(event.target.files?.[0] || null)}
                />
              </div>

              <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
                The complaint will be opened and assigned to you automatically.
              </p>
            </>
          )}
        </form>
      </PortalSheet>
        </div>
  )
}
